/**
 * Shared validation helpers for the eligibility assessment wizard.
 *
 * Every required field wrapper carries `fieldAnchorProps(name)` so the page can
 * scroll to and focus the first invalid field, and every control that fails
 * validation carries `invalidInputProps(name, message)` so assistive tech reads
 * the inline message next to the visible label.
 */

export const fieldAnchorId = (name) => `field-${name}`;
export const fieldErrorId = (name) => `${name}-error`;

/** Wrapper attributes for a field so the validation summary can link to it. */
export const fieldAnchorProps = (name) => ({
  id: fieldAnchorId(name),
  "data-field": name,
});

/** `aria-invalid` + `aria-describedby` for a control when it has an error. */
export const invalidInputProps = (name, message, extraDescribedBy = "") => {
  if (!message) return extraDescribedBy ? { "aria-describedby": extraDescribedBy } : {};
  const describedBy = [fieldErrorId(name), extraDescribedBy].filter(Boolean).join(" ");
  return { "aria-invalid": true, "aria-describedby": describedBy };
};

/**
 * Singapore UEN formats (ACRA): 8 digits + check letter (businesses registered
 * before 2009), 4-digit year + 5 digits + check letter (local companies), or
 * T/S/R + 2-digit year + 2-letter entity code + 4 digits + check letter.
 */
export const isValidSingaporeUen = (value) => {
  const v = String(value || "").trim().toUpperCase();
  if (!v) return false;
  return /^(\d{8}[A-Z]|\d{9}[A-Z]|[TSR]\d{2}[A-Z]{2}\d{4}[A-Z])$/.test(v);
};

const FOCUSABLE = 'input:not([type="hidden"]), textarea, select, button, [tabindex]:not([tabindex="-1"])';

/**
 * Scroll the first invalid field into view and move focus to its control (or
 * the wrapper when the field is a group). Returns true when an anchor exists.
 */
export const focusInvalidField = (name, { behavior = "smooth" } = {}) => {
  if (typeof document === "undefined" || !name) return false;
  const anchor = document.getElementById(fieldAnchorId(name));
  if (!anchor) return false;
  const control = anchor.matches(FOCUSABLE) ? anchor : anchor.querySelector(FOCUSABLE);
  const target = control || anchor;
  try {
    anchor.scrollIntoView({ behavior, block: "center" });
  } catch {
    anchor.scrollIntoView();
  }
  if (target && typeof target.focus === "function") {
    if (!control && !anchor.hasAttribute("tabindex")) anchor.setAttribute("tabindex", "-1");
    try {
      target.focus({ preventScroll: true });
    } catch {
      target.focus();
    }
  }
  return true;
};
